import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarClock, CheckCircle2, Mail, Bell, Loader2, Play, FileCheck, ArrowRight, TrendingUp } from 'lucide-react';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { toast } from 'sonner';

// iter898 — Mirror of the backend's per-country VAT filing defaults.
// Duplicated intentionally (rather than fetching) so the picker
// applies changes instantly without a network round-trip. Keep in
// sync with backend/routers/vat_tracker.py::COUNTRY_DEFAULTS.
// iter898 — Mirror of the backend's per-country VAT filing defaults.
// iter917 — Extended with `currency` so the picker auto-fills the
// tenant's base currency (SAR for KSA, AED for UAE, GBP for UK, …).
const COUNTRY_DEFAULTS = {
  sa: { filing_frequency: 'monthly',   grace_days: 30, currency: 'SAR' },
  ae: { filing_frequency: 'quarterly', grace_days: 28, currency: 'AED' },
  bh: { filing_frequency: 'quarterly', grace_days: 30, currency: 'BHD' },
  om: { filing_frequency: 'quarterly', grace_days: 30, currency: 'OMR' },
  kw: { filing_frequency: 'quarterly', grace_days: 30, currency: 'KWD' },
  qa: { filing_frequency: 'quarterly', grace_days: 30, currency: 'QAR' },
  eg: { filing_frequency: 'monthly',   grace_days: 30, currency: 'EGP' },
  gb: { filing_frequency: 'quarterly', grace_days: 35, currency: 'GBP' },
  in: { filing_frequency: 'monthly',   grace_days: 20, currency: 'INR' },
  us: { filing_frequency: 'monthly',   grace_days: 30, currency: 'USD' },
  pk: { filing_frequency: 'monthly',   grace_days: 15, currency: 'PKR' },
  '*': { filing_frequency: 'monthly',  grace_days: 30, currency: 'USD' },
};

/**
 * iter897 — VAT Filing Tracker dashboard.
 *
 * Big countdown ring to the next ZATCA filing deadline + inline
 * config for filing frequency (monthly/quarterly) + accountant email
 * + one-tap "Mark Filed" + history of previous filings + audit trail
 * of reminders sent for the current period.
 */
// iter920 — Timeline step used inside the Webhook Timeline drawer.
// Renders a coloured dot + connecting line, an event label, and a
// timestamp. `status` picks the palette (sent=emerald, opened=violet,
// bounced=amber/rose, info=slate).
function TimelineStep({ event, at, detail, status }) {
  const palette = {
    sent: 'bg-emerald-500 border-emerald-300',
    opened: 'bg-violet-500 border-violet-300',
    bounced: 'bg-rose-500 border-rose-300',
    info: 'bg-sky-500 border-sky-300',
  }[status] || 'bg-slate-400 border-slate-300';
  return (
    <div className="flex gap-3" data-testid={`timeline-step-${event}`}>
      <div className="flex flex-col items-center pt-0.5">
        <div className={`w-3 h-3 rounded-full border-2 border-white shadow ${palette}`} />
        <div className="w-px flex-1 bg-muted mt-1" />
      </div>
      <div className="flex-1 min-w-0 pb-2">
        <div className="text-sm font-medium">{event}</div>
        {at && <div className="text-[11px] text-muted-foreground">{new Date(at).toLocaleString()}</div>}
        {detail && <div className="text-[11px] text-muted-foreground/80 mt-0.5">{detail}</div>}
      </div>
    </div>
  );
}

export default function VATTrackerPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filing, setFiling] = useState(false);
  const [filingRef, setFilingRef] = useState('');
  const [filingNotes, setFilingNotes] = useState('');

  const [freq, setFreq] = useState('monthly');
  const [accountantEmail, setAccountantEmail] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [country, setCountry] = useState('sa');
  const [graceDays, setGraceDays] = useState(30);
  // iter917 — Base currency (auto-set from country picker but user can override).
  const [tenantCurrency, setTenantCurrency] = useState('SAR');
  // iter917 — Reminder Delivery Log (last N reminders + status summary).
  const [reminderLog, setReminderLog] = useState({ reminders: [], summary: null, loading: false });
  // iter920 — HMRC MTD live-submission status (only meaningful when country=gb).
  const [hmrcStatus, setHmrcStatus] = useState(null);
  // iter920 — Webhook timeline drawer state (open when a reminder-log row is clicked).
  const [timelineDrawer, setTimelineDrawer] = useState({ open: false, reminder: null });
  // iter922 — Retry-to-accountant panel state.
  const [retryEmail, setRetryEmail] = useState('');
  const [retryLoading, setRetryLoading] = useState(false);
  // iter924 — Live countdown when the 3-per-hour limit is hit.
  const [retryCooldown, setRetryCooldown] = useState(null); // { until: Date, remaining_s }

  // iter924 — Tick the countdown once per second so the UI decrements
  // "Try again in 42m 15s" without a full re-fetch.
  useEffect(() => {
    if (!retryCooldown?.until) return undefined;
    const t = setInterval(() => {
      const secs = Math.max(0, Math.floor((new Date(retryCooldown.until) - new Date()) / 1000));
      if (secs === 0) setRetryCooldown(null);
      else setRetryCooldown((c) => ({ ...c, remaining_s: secs }));
    }, 1000);
    return () => clearInterval(t);
  }, [retryCooldown?.until]);
  // iter924 — Filing Receipts state (past Marked-Filed PDFs).
  const [filings, setFilings] = useState([]);
  const [filingsLoading, setFilingsLoading] = useState(false);
  // iter927 — Filing Receipts filters (period range + free text).
  const [filingsFilters, setFilingsFilters] = useState({ from: '', to: '', search: '' });
  const [filingsCsvBusy, setFilingsCsvBusy] = useState(false);
  // iter925 — HMRC receipt mini-drawer (opens on the "HMRC ↗" chip).
  const [hmrcReceiptDrawer, setHmrcReceiptDrawer] = useState(null);
  const fetchFilings = async (overrides) => {
    setFilingsLoading(true);
    try {
      const f = { ...filingsFilters, ...(overrides || {}) };
      const params = { limit: 50 };
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;
      if (f.search && f.search.trim()) params.search = f.search.trim();
      const { data } = await api.get('/vat-tracker/filings', { params });
      setFilings(data?.filings || []);
    } catch { setFilings([]); }
    finally { setFilingsLoading(false); }
  };
  // iter927 — Trigger a CSV download using the same filter set.
  const exportFilingsCsv = async () => {
    setFilingsCsvBusy(true);
    try {
      const params = new URLSearchParams();
      if (filingsFilters.from) params.set('from', filingsFilters.from);
      if (filingsFilters.to) params.set('to', filingsFilters.to);
      if (filingsFilters.search && filingsFilters.search.trim())
        params.set('search', filingsFilters.search.trim());
      const res = await api.get(`/vat-tracker/filings/csv${params.toString() ? '?' + params.toString() : ''}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      const dl = URL.createObjectURL(blob);
      link.href = dl;
      link.download = `vat-filings-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(dl);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to export CSV');
    } finally { setFilingsCsvBusy(false); }
  };
  useEffect(() => { fetchFilings(); }, []);

  const fetchStatus = async () => {
    try {
      const { data: res } = await api.get('/vat-tracker/status');
      setData(res);
      setFreq(res?.config?.filing_frequency || 'monthly');
      setAccountantEmail(res?.config?.accountant_email || '');
      setSenderEmail(res?.config?.sender_email || '');
      setCountry(res?.config?.country || 'sa');
      setGraceDays(res?.config?.grace_days || 30);
      setTenantCurrency(res?.config?.currency || 'SAR');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load VAT tracker');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchStatus(); }, []);

  // iter917 — Reminder Delivery Log fetch. Runs on mount and after a
  // manual "Run reminders now" so the operator sees the fresh outcome.
  const fetchReminderLog = async () => {
    setReminderLog((s) => ({ ...s, loading: true }));
    try {
      const { data: res } = await api.get('/vat-tracker/reminder-log', { params: { limit: 30 } });
      setReminderLog({ reminders: res?.reminders || [], summary: res?.summary || null, loading: false });
    } catch (e) {
      setReminderLog({ reminders: [], summary: null, loading: false });
    }
  };
  useEffect(() => { fetchReminderLog(); }, []);

  // iter920 — Load HMRC MTD status so the UK tenant sees a Connect /
  // Submit CTA embedded in the VAT Tracker rather than being sent
  // to a separate page. Silently ignore failures — HMRC MTD is
  // optional and unavailable outside the UK.
  const fetchHmrcStatus = async () => {
    try {
      const { data } = await api.get('/hmrc-mtd/config');
      setHmrcStatus(data || null);
    } catch { setHmrcStatus(null); }
  };
  useEffect(() => { fetchHmrcStatus(); }, []);

  // iter929 — Cron Health Dashboard state. Surfaces the
  // `hmrc_dry_runs_purge_log` audit trail written by both the nightly
  // 03:15 UTC cron (via .emergent/crons.yml) and manual "Fire Now"
  // triggers. Admin-only endpoint — non-admins simply see empty state.
  const [cronHealth, setCronHealth] = useState(null);
  const [cronHealthLoading, setCronHealthLoading] = useState(false);
  const [fireNowBusy, setFireNowBusy] = useState(false);
  // Ticker for the "next fire in Xh Ym" countdown — updates once/sec.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!cronHealth?.next_fire_utc) return undefined;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [cronHealth?.next_fire_utc]);
  const fetchCronHealth = async () => {
    setCronHealthLoading(true);
    try {
      const { data } = await api.get('/hmrc-mtd/purge-cron/health');
      setCronHealth(data || null);
    } catch { setCronHealth(null); }
    finally { setCronHealthLoading(false); }
  };
  useEffect(() => { fetchCronHealth(); }, []);
  const fireCronNow = async () => {
    if (!window.confirm('Run the HMRC dry-run purge immediately for THIS tenant?\n\nRows older than 30 days will be deleted. This is safe and audit-logged.')) return;
    setFireNowBusy(true);
    try {
      const { data } = await api.post('/hmrc-mtd/purge-cron/fire-now?days=30');
      toast.success(`Purge fired · ${data?.deleted ?? 0} row(s) deleted`);
      await fetchCronHealth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fire Now failed');
    } finally { setFireNowBusy(false); }
  };
  const formatEta = (isoTs) => {
    if (!isoTs) return '—';
    const target = new Date(isoTs).getTime();
    const secs = Math.max(0, Math.floor((target - nowTick) / 1000));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.post('/vat-tracker/config', {
        filing_frequency: freq,
        accountant_email: accountantEmail,
        sender_email: senderEmail,
        country,
        grace_days: Number(graceDays),
        currency: tenantCurrency,
      });
      toast.success('Filing preferences saved');
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const markFiled = async () => {
    setFiling(true);
    try {
      await api.post('/vat-tracker/mark-filed', {
        reference: filingRef || null,
        notes: filingNotes || null,
        net_vat_filed: data?.net_vat_estimate,
      });
      toast.success('Filing recorded — reminders stopped for this period');
      setFilingRef('');
      setFilingNotes('');
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mark-filed failed');
    } finally {
      setFiling(false);
    }
  };

  const runNow = async () => {
    try {
      const { data: r } = await api.post('/vat-tracker/run-reminders');
      toast.success(`Ran cron — sent ${r.reminders_sent} reminder${r.reminders_sent === 1 ? '' : 's'} · ${r.emails} email${r.emails === 1 ? '' : 's'}`);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Manual run failed');
    }
  };

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="animate-spin mr-2" /> Loading VAT tracker…</div></DashboardLayout>;
  }
  if (!data) return null;

  const daysLeft = data.days_left;
  const filed = data.filed;
  const urgency = data.urgency;
  const ringColor = filed
    ? 'text-emerald-500'
    : urgency === 'critical' || urgency === 'overdue' ? 'text-rose-500'
    : urgency === 'warning' ? 'text-amber-500'
    : urgency === 'info' ? 'text-sky-500'
    : 'text-emerald-500';
  const ringBg = filed
    ? 'from-emerald-500 to-teal-600'
    : urgency === 'critical' || urgency === 'overdue' ? 'from-rose-500 to-red-600'
    : urgency === 'warning' ? 'from-amber-500 to-orange-600'
    : urgency === 'info' ? 'from-sky-500 to-blue-600'
    : 'from-emerald-500 to-teal-600';
  // Countdown ring: 0..30 days maps to 0..100% of the circumference.
  const pct = Math.max(0, Math.min(100, filed ? 100 : Math.round(((30 - Math.max(0, Math.min(30, daysLeft))) / 30) * 100)));
  const currency = data.config?.currency || 'SAR';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vat-tracker-page">
        {/* iter919 — Resend webhook secret nudge. Only visible when
             the receiver is getting real events but the secret isn't
             configured — meaning delivery-log flips aren't signature-
             verified. Dismissable per session via localStorage. */}
        {data?.webhook_status?.should_nudge && localStorage.getItem('rl_nudge_dismissed') !== '1' && (
          <div className="rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 p-4 flex items-start gap-3" data-testid="webhook-secret-nudge">
            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center shrink-0 text-amber-700">⚠</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-amber-900 dark:text-amber-100">
                Resend webhook secret not configured
              </p>
              <p className="text-xs text-amber-800/90 dark:text-amber-200/80 mt-0.5">
                We&apos;ve received <b data-testid="webhook-nudge-count">{data.webhook_status.recent_events_7d}</b> Resend webhook event{data.webhook_status.recent_events_7d === 1 ? '' : 's'} in the last 7 days, but <code className="px-1 rounded bg-amber-100 dark:bg-amber-900/60 text-[10px]">RESEND_WEBHOOK_SECRET</code> is unset. Delivery-log statuses (bounced / opened) are being written <b>without signature verification</b> — a malicious sender could forge events.
              </p>
              <ol className="text-xs text-amber-800/90 dark:text-amber-200/80 mt-2 space-y-0.5 list-decimal ml-4">
                <li>Open <a href="https://resend.com/webhooks" target="_blank" rel="noreferrer" className="underline font-medium">resend.com/webhooks</a> → your endpoint</li>
                <li>Copy the <code className="text-[10px]">whsec_…</code> signing secret</li>
                <li>Paste into <code className="text-[10px]">backend/.env</code> as <code className="text-[10px]">RESEND_WEBHOOK_SECRET=whsec_…</code></li>
                <li>Restart the backend: <code className="text-[10px]">supervisorctl restart backend</code></li>
              </ol>
            </div>
            <button
              className="text-xs text-amber-800 hover:text-amber-900 dark:text-amber-200 dark:hover:text-amber-100 underline shrink-0"
              onClick={() => { try { localStorage.setItem('rl_nudge_dismissed', '1'); } catch { /* ignore */ } fetchStatus(); }}
              data-testid="webhook-nudge-dismiss"
            >
              Dismiss
            </button>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${ringBg} flex items-center justify-center shadow-lg`}>
            <CalendarClock size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight">VAT Filing Tracker</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Automated ZATCA deadline reminders · in-app + email at T-15, T-7, T-1 days</p>
          </div>
          {/* iter920 — Delivery Health Score widget. Rolls up the last 30
               days of reminders into a single deliverability % + a per-status
               mini strip. Hidden when there are no reminders yet. */}
          {reminderLog.summary?.total > 0 && (() => {
            const s = reminderLog.summary;
            const denom = (s.sent || 0) + (s.opened || 0) + (s.bounced || 0) + (s.failed || 0);
            const good = (s.sent || 0) + (s.opened || 0);
            const score = denom ? Math.round((good / denom) * 100) : 0;
            const tone = score >= 90 ? 'emerald' : score >= 70 ? 'amber' : 'rose';
            const toneMap = {
              emerald: 'bg-emerald-100 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200',
              amber: 'bg-amber-100 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-200',
              rose: 'bg-rose-100 dark:bg-rose-950/40 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-200',
            };
            return (
              <div className={`ml-auto flex items-center gap-3 rounded-xl border px-4 py-2 ${toneMap[tone]}`} data-testid="delivery-health-widget">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">Delivery Health</div>
                  <div className="text-2xl font-bold leading-none" data-testid="delivery-health-score">{score}<span className="text-sm">%</span></div>
                  <div className="text-[10px] opacity-70 mt-0.5">last {s.total} reminders</div>
                </div>
                <div className="flex flex-col gap-0.5 text-[10px]">
                  <span title="Sent + Opened">✓ {good}</span>
                  <span title="Bounced">↩ {s.bounced || 0}</span>
                  <span title="Failed">✗ {s.failed || 0}</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Countdown card */}
        <Card className={`overflow-hidden ${filed ? 'border-emerald-300 dark:border-emerald-800' : ''}`}>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              {/* Ring */}
              <div className="flex justify-center">
                <div className="relative w-52 h-52">
                  <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="10" className="text-muted/40" />
                    <circle
                      cx="60" cy="60" r="52" fill="none"
                      stroke="currentColor" strokeWidth="10" strokeLinecap="round"
                      strokeDasharray={`${(pct / 100) * (2 * Math.PI * 52)} ${2 * Math.PI * 52}`}
                      className={`${ringColor} transition-all duration-1000`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {filed ? (
                      <>
                        <CheckCircle2 size={40} className="text-emerald-500 mb-1" />
                        <div className="text-lg font-bold text-emerald-700">Filed</div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">for {data.current_period.label}</div>
                      </>
                    ) : (
                      <>
                        <div className={`text-6xl font-bold ${ringColor}`} data-testid="vat-tracker-days-left">
                          {daysLeft < 0 ? Math.abs(daysLeft) : daysLeft}
                        </div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">
                          {daysLeft < 0 ? 'days overdue' : daysLeft === 1 ? 'day left' : 'days left'}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Return period</div>
                  <div className="text-lg font-semibold" data-testid="vat-tracker-period-label">{data.current_period.label}</div>
                  <div className="text-xs text-muted-foreground">{data.current_period.start} → {data.current_period.end}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Due date</div>
                  <div className="text-lg font-semibold" data-testid="vat-tracker-due-date">
                    {new Date(data.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', weekday: 'short' })}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Estimated Net VAT</div>
                  <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                    {currency} {Math.abs(data.net_vat_estimate || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                {!filed && (
                  <div className="pt-2 flex flex-wrap gap-2">
                    <Button
                      onClick={markFiled}
                      disabled={filing}
                      className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
                      data-testid="vat-tracker-mark-filed-btn"
                    >
                      {filing ? <Loader2 className="animate-spin" size={14} /> : <FileCheck size={14} />}
                      Mark as Filed
                    </Button>
                    <Button variant="outline" onClick={() => window.location.href = '/vat-summary'} className="gap-1.5">
                      Open VAT Summary <ArrowRight size={14} />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* iter920 — HMRC MTD live-submission card. Only shown when
             country=gb (UK VAT tenants). Provides a Connect / Disconnect
             flow + deep-link to the full HMRC MTD page for submission.
             When HMRC credentials aren't yet configured on the backend,
             the card explains the setup path instead of a Connect button. */}
        {country === 'gb' && (
          <Card className="border-2 border-sky-200 dark:border-sky-800/60 bg-sky-50/40 dark:bg-sky-950/20" data-testid="hmrc-mtd-card">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    🇬🇧 HMRC MTD — Live Submission
                  </h2>
                  <p className="text-xs text-muted-foreground">File your UK VAT return directly to HMRC&apos;s Making Tax Digital API without leaving this page.</p>
                </div>
                {hmrcStatus?.configured && hmrcStatus?.authorized ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium border border-emerald-200" data-testid="hmrc-status-connected">
                    ✓ Connected · {hmrcStatus.environment || 'sandbox'}
                  </span>
                ) : hmrcStatus?.configured ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium border border-amber-200" data-testid="hmrc-status-configured">
                    ⚠ Credentials saved · Authorise required
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200" data-testid="hmrc-status-notconfigured">
                    Not configured
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center pt-1">
                {hmrcStatus?.configured && hmrcStatus?.authorized ? (
                  <>
                    <a
                      href="/hmrc-mtd"
                      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium transition"
                      data-testid="hmrc-open-submit"
                    >
                      Open HMRC submission →
                    </a>
                    <span className="text-xs text-muted-foreground">
                      VRN {hmrcStatus.vrn || '—'}
                      {hmrcStatus.token_expires_at && ` · token exp ${new Date(hmrcStatus.token_expires_at).toLocaleDateString()}`}
                    </span>
                  </>
                ) : hmrcStatus?.configured ? (
                  <a
                    href="/hmrc-mtd"
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition"
                    data-testid="hmrc-authorize-cta"
                  >
                    Authorise with HMRC →
                  </a>
                ) : (
                  <a
                    href="/hmrc-mtd"
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium transition"
                    data-testid="hmrc-connect-cta"
                  >
                    Set up HMRC MTD →
                  </a>
                )}
                <p className="text-[11px] text-muted-foreground/80 flex-1 min-w-[240px]">
                  You&apos;ll need your HMRC Developer client_id + client_secret and 9-digit VRN. Sandbox mode is available for testing before switching to production.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* iter929 — Cron Health Dashboard.
             Surfaces the hmrc_dry_runs_purge_log written by both the
             nightly 03:15 UTC cron (via `.emergent/crons.yml`) and
             manual "Fire Now" triggers, so admins can VERIFY the new
             crons.yml wiring is actually working end-to-end.

             Rendered only when the health payload comes back OK
             (admin-only endpoint returns 403 for non-admins). */}
        {cronHealth && (
          <Card className="border-2 border-amber-200/70 dark:border-amber-800/50 bg-amber-50/30 dark:bg-amber-950/10" data-testid="cron-health-card">
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    ⏰ Auto-Purge Cron Health
                    {cronHealth.secret_configured
                      ? <Badge variant="outline" className="text-[10px] border-emerald-400 text-emerald-700 dark:text-emerald-300">secret configured</Badge>
                      : <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-300">secret missing</Badge>}
                  </h3>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {cronHealth.schedule?.human || 'Every day at 03:15 UTC'} · deletes HMRC dry-run rows older than 30 days
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px]"
                    onClick={fetchCronHealth}
                    disabled={cronHealthLoading}
                    data-testid="cron-health-refresh-btn"
                  >
                    {cronHealthLoading ? 'Loading…' : 'Refresh'}
                  </Button>
                  <Button
                    size="sm"
                    variant="default"
                    className="h-7 text-[11px]"
                    onClick={fireCronNow}
                    disabled={fireNowBusy}
                    data-testid="cron-fire-now-btn"
                    title="Trigger the purge for this tenant right now (audit-logged)"
                  >
                    {fireNowBusy ? 'Firing…' : '⚡ Fire Now'}
                  </Button>
                </div>
              </div>

              {/* Summary strip: next fire ETA + totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <div className="rounded border bg-background/60 px-2 py-1.5" data-testid="cron-health-next-fire">
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Next fire</div>
                  <div className="font-semibold tabular-nums">{formatEta(cronHealth.next_fire_utc)}</div>
                  <div className="text-[9px] text-muted-foreground truncate">
                    {cronHealth.next_fire_utc ? new Date(cronHealth.next_fire_utc).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' }) : '—'}
                  </div>
                </div>
                <div className="rounded border bg-background/60 px-2 py-1.5" data-testid="cron-health-last-run">
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Last run</div>
                  <div className="font-semibold">
                    {cronHealth.last_runs?.[0]?.ran_at
                      ? new Date(cronHealth.last_runs[0].ran_at).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })
                      : '—'}
                  </div>
                  <div className="text-[9px] text-muted-foreground capitalize">
                    channel: {cronHealth.last_runs?.[0]?.channel || '—'}
                  </div>
                </div>
                <div className="rounded border bg-background/60 px-2 py-1.5" data-testid="cron-health-deleted-total">
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Rows kept out</div>
                  <div className="font-semibold tabular-nums">{cronHealth.totals?.deleted_all_time?.toLocaleString() ?? 0}</div>
                  <div className="text-[9px] text-muted-foreground">across all runs</div>
                </div>
                <div className="rounded border bg-background/60 px-2 py-1.5" data-testid="cron-health-runs-30d">
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Runs 30d / all-time</div>
                  <div className="font-semibold tabular-nums">
                    {cronHealth.totals?.last_30d ?? 0} <span className="text-muted-foreground">/</span> {cronHealth.totals?.all_time ?? 0}
                  </div>
                  <div className="text-[9px] text-muted-foreground truncate">{cronHealth.schedule?.cron || ''}</div>
                </div>
              </div>

              {/* Recent runs table */}
              {cronHealth.last_runs?.length > 0 ? (
                <div className="border rounded overflow-hidden" data-testid="cron-health-runs-table">
                  <div className="grid grid-cols-12 gap-1 px-2 py-1 text-[9px] uppercase tracking-wider text-muted-foreground bg-muted/40 border-b">
                    <div className="col-span-4">Ran at (UTC)</div>
                    <div className="col-span-3">Channel</div>
                    <div className="col-span-2 text-right">Deleted</div>
                    <div className="col-span-3 truncate">Actor / run id</div>
                  </div>
                  {cronHealth.last_runs.slice(0, 10).map((r) => (
                    <div key={r.id} className="grid grid-cols-12 gap-1 px-2 py-1 text-[11px] border-b last:border-b-0 hover:bg-muted/30">
                      <div className="col-span-4 tabular-nums">
                        {r.ran_at ? new Date(r.ran_at).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', month: 'short', day: 'numeric' }) : '—'}
                      </div>
                      <div className="col-span-3">
                        <Badge
                          variant="outline"
                          className={
                            r.channel === 'cron'
                              ? 'text-[9px] border-sky-300 text-sky-700 dark:text-sky-300'
                              : r.channel === 'fire_now'
                                ? 'text-[9px] border-amber-300 text-amber-700 dark:text-amber-300'
                                : 'text-[9px] border-slate-300 text-slate-700 dark:text-slate-300'
                          }
                        >
                          {r.channel || 'admin'}
                        </Badge>
                      </div>
                      <div className="col-span-2 text-right tabular-nums font-medium">{r.deleted ?? 0}</div>
                      <div className="col-span-3 text-[10px] text-muted-foreground truncate" title={r.actor_email || r.run_id || ''}>
                        {r.actor_email || r.run_id || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-muted-foreground italic border rounded px-3 py-2" data-testid="cron-health-empty">
                  No purge runs yet. Fire it manually to seed history — the nightly cron will pick up from tomorrow 03:15 UTC.
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Config */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Filing Preferences</h2>
                <p className="text-xs text-muted-foreground">How often you file and where reminders are sent.</p>
              </div>
              <Button variant="outline" size="sm" onClick={runNow} className="gap-1.5" data-testid="vat-tracker-run-now">
                <Play size={12} /> Run reminders now
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Country</label>
                <Select
                  value={country}
                  onValueChange={(c) => {
                    setCountry(c);
                    // iter898 — Auto-apply country defaults on change.
                    // iter917 — Also auto-sync the base currency (SAR
                    // for KSA, AED for UAE, GBP for UK, …). Users can
                    // still override any field after.
                    const defaults = COUNTRY_DEFAULTS[c] || COUNTRY_DEFAULTS['*'];
                    if (defaults) {
                      setFreq(defaults.filing_frequency);
                      setGraceDays(defaults.grace_days);
                      if (defaults.currency) setTenantCurrency(defaults.currency);
                    }
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="vat-tracker-country"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sa">🇸🇦 Saudi Arabia (ZATCA)</SelectItem>
                    <SelectItem value="ae">🇦🇪 United Arab Emirates (FTA)</SelectItem>
                    <SelectItem value="bh">🇧🇭 Bahrain (NBR)</SelectItem>
                    <SelectItem value="om">🇴🇲 Oman</SelectItem>
                    <SelectItem value="kw">🇰🇼 Kuwait</SelectItem>
                    <SelectItem value="qa">🇶🇦 Qatar</SelectItem>
                    <SelectItem value="eg">🇪🇬 Egypt</SelectItem>
                    <SelectItem value="gb">🇬🇧 United Kingdom (HMRC MTD)</SelectItem>
                    <SelectItem value="in">🇮🇳 India (GSTR)</SelectItem>
                    <SelectItem value="us">🇺🇸 United States</SelectItem>
                    <SelectItem value="pk">🇵🇰 Pakistan</SelectItem>
                    <SelectItem value="*">🌍 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Frequency</label>
                <Select value={freq} onValueChange={setFreq}>
                  <SelectTrigger className="mt-1" data-testid="vat-tracker-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Grace days after period end</label>
                <Input
                  type="number" min="1" max="90"
                  value={graceDays}
                  onChange={(e) => setGraceDays(e.target.value)}
                  className="mt-1"
                  data-testid="vat-tracker-grace-days"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Base currency <span className="text-[9px] text-muted-foreground/70 normal-case">(auto-set from country)</span>
                </label>
                <Input
                  value={tenantCurrency}
                  onChange={(e) => setTenantCurrency(e.target.value.toUpperCase().slice(0, 4))}
                  className="mt-1"
                  placeholder="SAR"
                  data-testid="vat-tracker-currency"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Accountant email</label>
                <Input
                  type="email"
                  value={accountantEmail}
                  onChange={(e) => setAccountantEmail(e.target.value)}
                  placeholder="cpa@example.com"
                  className="mt-1"
                  data-testid="vat-tracker-accountant-email"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-4">
                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Sender email (must be verified in Resend)
                </label>
                <Input
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="no-reply@yourdomain.com — leave blank to use the platform default"
                  className="mt-1"
                  data-testid="vat-tracker-sender-email"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline text-primary">Verify your domain in Resend →</a>
                  {' '}before setting this field, or reminder emails will bounce.
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={saveConfig} disabled={saving} data-testid="vat-tracker-save-config">
                {saving && <Loader2 className="animate-spin mr-2" size={14} />} Save Preferences
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Mark Filed extras + Reminders + History */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Reminders sent for this period */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-3">
                <Bell size={16} className="text-amber-500" />
                <h2 className="text-lg font-semibold">Reminders for This Period</h2>
                <Badge variant="secondary">{data.reminders_sent?.length || 0}</Badge>
              </div>
              {(!data.reminders_sent || data.reminders_sent.length === 0) ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No reminders sent yet. The cron will fire at T-{data.reminder_offsets.join(', T-')} days.
                </p>
              ) : (
                <ul className="space-y-2">
                  {data.reminders_sent.map((r) => (
                    <li key={r.id} className="flex items-center gap-3 py-1.5 border-b last:border-0" data-testid={`vat-tracker-reminder-${r.days_left}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${r.days_left <= 1 ? 'bg-rose-100 text-rose-700' : r.days_left <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>
                        <span className="text-xs font-bold">T-{r.days_left}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">
                          Sent on <b>{new Date(r.sent_at).toLocaleString()}</b>
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          {r.channel?.includes('email') && <><Mail size={10} /> email · </>}
                          <Bell size={10} /> in-app
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Filing history */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} className="text-emerald-500" />
                <h2 className="text-lg font-semibold">Filing History</h2>
                <Badge variant="secondary">{data.history?.length || 0}</Badge>
              </div>
              {(!data.history || data.history.length === 0) ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No filings yet. Once you tap &quot;Mark as Filed&quot; it appears here as an audit trail.
                </p>
              ) : (
                <ul className="space-y-2 max-h-72 overflow-y-auto">
                  {data.history.map((h) => (
                    <li key={h.id} className="flex items-center gap-3 py-2 border-b last:border-0" data-testid={`vat-tracker-history-${h.period_start}`}>
                      <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">{h.period_start} → {h.period_end}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          Filed {new Date(h.filed_at).toLocaleDateString()} {h.reference ? `· ref ${h.reference}` : ''}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {!filed && (
          <Card className="border-dashed">
            <CardContent className="pt-5">
              <h3 className="text-sm font-semibold mb-2">Record filing details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  placeholder="ZATCA reference number (optional)"
                  value={filingRef}
                  onChange={(e) => setFilingRef(e.target.value)}
                  data-testid="vat-tracker-filing-ref"
                />
                <Input
                  placeholder="Notes (optional)"
                  value={filingNotes}
                  onChange={(e) => setFilingNotes(e.target.value)}
                  data-testid="vat-tracker-filing-notes"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* iter917 — Reminder Delivery Log: per-tenant history of every
             VAT reminder fired, with a normalised delivery status
             (sent / in_app_only / failed / bounced / opened). Powered by
             GET /vat-tracker/reminder-log. */}
        <Card data-testid="reminder-delivery-log">
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <CalendarClock size={14} /> Reminder Delivery Log
                {reminderLog.summary?.total > 0 && (
                  <span className="text-[10px] text-muted-foreground font-normal">
                    · {reminderLog.summary.total} total
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2 text-[11px]">
                {reminderLog.summary && (
                  <>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="rl-count-sent">
                      Sent {reminderLog.summary.sent || 0}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200" data-testid="rl-count-inapp">
                      In-app only {reminderLog.summary.in_app_only || 0}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 border border-rose-200" data-testid="rl-count-failed">
                      Failed {reminderLog.summary.failed || 0}
                    </span>
                    {reminderLog.summary.bounced ? (
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200" data-testid="rl-count-bounced">
                        Bounced {reminderLog.summary.bounced}
                      </span>
                    ) : null}
                    {reminderLog.summary.opened ? (
                      <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 border border-violet-200" data-testid="rl-count-opened">
                        Opened {reminderLog.summary.opened}
                      </span>
                    ) : null}
                  </>
                )}
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={fetchReminderLog} disabled={reminderLog.loading} data-testid="rl-refresh-btn">
                  {reminderLog.loading ? 'Loading…' : 'Refresh'}
                </Button>
              </div>
            </div>
            {reminderLog.reminders.length === 0 ? (
              <div className="text-center text-xs text-muted-foreground py-6 rounded-lg bg-muted/40 border border-dashed">
                No reminders have been sent yet. They fire automatically at 07:00 UTC on the day of a T-15 / T-7 / T-1 deadline.
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto rounded-lg border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">Sent at</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Period</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Days left</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Recipient</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reminderLog.reminders.map((r) => {
                      const badge =
                        r.status === 'sent' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' :
                        r.status === 'opened' ? 'bg-violet-100 text-violet-800 border-violet-200' :
                        r.status === 'bounced' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                        r.status === 'failed' ? 'bg-rose-100 text-rose-800 border-rose-200' :
                        'bg-sky-100 text-sky-800 border-sky-200';
                      return (
                        <tr
                          key={r.id}
                          className="border-t hover:bg-muted/20 cursor-pointer"
                          data-testid={`rl-row-${r.id}`}
                          onClick={() => setTimelineDrawer({ open: true, reminder: r })}
                          title="Click to see the full webhook timeline"
                        >
                          <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">
                            {r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}
                          </td>
                          <td className="px-2 py-1.5 whitespace-nowrap">{r.period_key || `${r.period_start} → ${r.period_end}`}</td>
                          <td className="px-2 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded ${r.days_left <= 1 ? 'bg-rose-50 text-rose-700' : r.days_left <= 7 ? 'bg-amber-50 text-amber-700' : 'bg-sky-50 text-sky-700'}`}>
                              T-{r.days_left ?? '?'}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[220px]">{r.recipient || '—'}</td>
                          <td className="px-2 py-1.5">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${badge}`} data-testid={`rl-status-${r.id}`}>
                              {r.status}
                            </span>
                            {r.error && (
                              <span className="ml-1 text-[10px] text-rose-600 truncate inline-block max-w-[180px] align-middle" title={r.error}>
                                {r.error}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* iter924 — Filing Receipts: list of every past Marked-Filed
             return with a one-click PDF re-download. Uses the same
             reportlab helper the mark-filed auto-email attaches. */}
        <Card data-testid="filing-receipts-card">
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                📄 Filing Receipts
                {filings.length > 0 && <span className="text-[10px] text-muted-foreground font-normal">· {filings.length} filed</span>}
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={exportFilingsCsv}
                  disabled={filingsCsvBusy || filings.length === 0}
                  data-testid="filings-export-csv-btn"
                  title="Download the currently-filtered filings as CSV"
                >
                  {filingsCsvBusy ? 'Exporting…' : '⇩ Export CSV'}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => fetchFilings()} disabled={filingsLoading} data-testid="filings-refresh-btn">
                  {filingsLoading ? 'Loading…' : 'Refresh'}
                </Button>
              </div>
            </div>

            {/* iter927 — Period-range + text search. Filters the local
                 list AND the CSV export. Left-aligned so filter state
                 sits above the results, mirroring accounting-report UX. */}
            <div className="flex flex-wrap items-end gap-2 pt-1">
              <div className="flex flex-col">
                <label className="text-[10px] text-muted-foreground mb-0.5">From (period start ≥)</label>
                <input
                  type="date"
                  value={filingsFilters.from}
                  onChange={(e) => setFilingsFilters((s) => ({ ...s, from: e.target.value }))}
                  className="h-7 text-[11px] border rounded px-1.5 bg-background"
                  data-testid="filings-filter-from"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-[10px] text-muted-foreground mb-0.5">To (period end ≤)</label>
                <input
                  type="date"
                  value={filingsFilters.to}
                  onChange={(e) => setFilingsFilters((s) => ({ ...s, to: e.target.value }))}
                  className="h-7 text-[11px] border rounded px-1.5 bg-background"
                  data-testid="filings-filter-to"
                />
              </div>
              <div className="flex flex-col flex-1 min-w-[160px]">
                <label className="text-[10px] text-muted-foreground mb-0.5">Search (ref / period / filed-by / HMRC bundle)</label>
                <input
                  type="text"
                  placeholder="e.g. HMRC-2024-Q3 or admin@…"
                  value={filingsFilters.search}
                  onChange={(e) => setFilingsFilters((s) => ({ ...s, search: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchFilings(); }}
                  className="h-7 text-[11px] border rounded px-2 bg-background"
                  data-testid="filings-filter-search"
                />
              </div>
              <Button
                size="sm"
                variant="default"
                className="h-7 text-[11px]"
                onClick={() => fetchFilings()}
                disabled={filingsLoading}
                data-testid="filings-apply-filters-btn"
              >
                Apply
              </Button>
              {(filingsFilters.from || filingsFilters.to || filingsFilters.search) && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-[11px]"
                  onClick={() => { setFilingsFilters({ from: '', to: '', search: '' }); fetchFilings({ from: '', to: '', search: '' }); }}
                  data-testid="filings-clear-filters-btn"
                >
                  Clear
                </Button>
              )}
            </div>
            {filings.length === 0 ? (
              <div className="text-center text-xs text-muted-foreground py-6 rounded-lg bg-muted/40 border border-dashed">
                No filings recorded yet. Every time you press <b>Mark this period as filed</b>, a PDF receipt with an SHA-256 audit signature is generated and stored here for re-download.
              </div>
            ) : (
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">Period</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Filed</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Reference</th>
                      <th className="text-right px-2 py-1.5 font-semibold">Net VAT</th>
                      <th className="text-center px-2 py-1.5 font-semibold w-24">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filings.map((f) => (
                      <tr key={f.id} className="border-t hover:bg-muted/20" data-testid={`filing-row-${f.id}`}>
                        <td className="px-2 py-1.5 whitespace-nowrap">{f.period_start} → {f.period_end}</td>
                        <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{f.filed_at ? new Date(f.filed_at).toLocaleDateString() : '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">
                          {f.reference || '—'}
                          {f.hmrc_receipt?.processingDate && f.hmrc_receipt?.formBundleNumber && (
                            <button
                              className="ml-1 px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200 text-[9px] hover:bg-sky-200"
                              data-testid={`filing-hmrc-receipt-${f.id}`}
                              onClick={() => setHmrcReceiptDrawer(f)}
                              title="View HMRC receipt (processingDate + formBundleNumber)"
                            >
                              HMRC ↗
                            </button>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono">{cur(tenantCurrency)} {Number(f.net_vat_filed || 0).toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/vat-tracker/filings/${f.id}/pdf`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-0.5 text-sky-700 hover:text-sky-900 hover:underline text-[11px]"
                            data-testid={`filing-pdf-${f.id}`}
                          >
                            ↓ PDF
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-[11px] text-muted-foreground text-center pb-4">
          Reminders fire automatically at 07:00 UTC daily.
          Email delivery uses Resend and requires an accountant email + verified sender domain for production filings.
        </p>

        {/* iter925 — HMRC receipt mini-modal. Shows the small receipt
             scalars (processingDate, formBundleNumber, paymentIndicator,
             chargeRefNumber) that HMRC returned when the filing was
             submitted via MTD. Read-only. */}
        {hmrcReceiptDrawer && (
          <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setHmrcReceiptDrawer(null)} data-testid="hmrc-receipt-modal-backdrop">
            <div className="bg-background rounded-2xl border shadow-2xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="hmrc-receipt-modal">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-base flex items-center gap-2">🇬🇧 HMRC MTD Receipt</h3>
                <button onClick={() => setHmrcReceiptDrawer(null)} className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center" data-testid="hmrc-receipt-close">✕</button>
              </div>
              <p className="text-xs text-muted-foreground">Values as HMRC&apos;s MTD-VAT API returned them at submission time.</p>
              <div className="grid grid-cols-1 gap-2 text-xs">
                {['processingDate', 'formBundleNumber', 'paymentIndicator', 'chargeRefNumber'].map((k) => (
                  <div key={k} className="rounded-lg border p-2 bg-muted/30">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{k}</div>
                    <div className="font-mono text-[12px] break-all" data-testid={`hmrc-receipt-${k}`}>
                      {hmrcReceiptDrawer.hmrc_receipt?.[k] ?? '—'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t text-[11px] text-muted-foreground">
                <b>Period:</b> {hmrcReceiptDrawer.period_start} → {hmrcReceiptDrawer.period_end}
                <br /><b>Reference:</b> {hmrcReceiptDrawer.reference || '—'}
              </div>
            </div>
          </div>
        )}

        {/* iter920 — Webhook Timeline drawer. Slides in from the right
             when a reminder row is clicked. Shows the full sequence of
             events fired by Resend: sent → opened → clicked → bounced
             etc., with timestamps. Backed by the `webhook_events[]`
             array pushed into vat_reminders_sent by the webhook receiver. */}
        {timelineDrawer.open && timelineDrawer.reminder && (
          <div
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex justify-end"
            onClick={() => setTimelineDrawer({ open: false, reminder: null })}
            data-testid="webhook-timeline-backdrop"
          >
            <div
              className="w-full max-w-md h-full bg-background border-l shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="webhook-timeline-drawer"
            >
              <div className="sticky top-0 bg-background border-b px-5 py-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-base">Delivery Timeline</h3>
                  <p className="text-xs text-muted-foreground truncate max-w-[280px]">
                    {timelineDrawer.reminder.recipient || 'in-app only'} · {timelineDrawer.reminder.period_key}
                  </p>
                </div>
                <button
                  onClick={() => setTimelineDrawer({ open: false, reminder: null })}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                  data-testid="webhook-timeline-close"
                >
                  ✕
                </button>
              </div>
              <div className="p-5 space-y-4">
                {/* Base "sent" event — always first. */}
                <TimelineStep
                  event="Reminder created"
                  at={timelineDrawer.reminder.sent_at}
                  detail={`T-${timelineDrawer.reminder.days_left} · channel=${timelineDrawer.reminder.channel}`}
                  status="sent"
                />
                {/* Resend webhook events appended in order. */}
                {(timelineDrawer.reminder.webhook_events || []).map((e, i) => (
                  <TimelineStep
                    key={i}
                    event={e.event}
                    at={e.at}
                    detail={e.signature_ok ? 'signature verified' : 'signature not verified (secret unset)'}
                    status={
                      e.event === 'email.bounced' || e.event === 'email.complained' ? 'bounced' :
                      e.event === 'email.opened' || e.event === 'email.clicked' ? 'opened' :
                      e.event === 'email.delivered' ? 'sent' : 'info'
                    }
                  />
                ))}
                {(!timelineDrawer.reminder.webhook_events || timelineDrawer.reminder.webhook_events.length === 0) && (
                  <div className="text-xs text-muted-foreground text-center py-6 rounded-lg bg-muted/40 border border-dashed">
                    No webhook events received for this reminder yet. Configure the Resend webhook (see banner above) to see live delivery updates here.
                  </div>
                )}
                {timelineDrawer.reminder.error && (
                  <div className="text-xs rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 p-3">
                    <div className="font-semibold text-rose-800 dark:text-rose-200">Last error</div>
                    <div className="text-rose-700 dark:text-rose-300 mt-0.5 break-words">{timelineDrawer.reminder.error}</div>
                  </div>
                )}
                {/* iter922 — Resend to accountant. Available when the
                     original bounced / failed. Optional override input
                     lets the operator fix the recipient address before
                     firing again. */}
                {(timelineDrawer.reminder.status === 'bounced' || timelineDrawer.reminder.status === 'failed') && (
                  <div className="rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50/60 dark:bg-sky-950/30 p-3 space-y-2" data-testid="timeline-retry-panel">
                    <div className="text-xs font-semibold text-sky-800 dark:text-sky-200">Resend to accountant</div>
                    <p className="text-[11px] text-sky-700/90 dark:text-sky-300/90">
                      Fixed the accountant&apos;s address? Enter the corrected email (or leave blank to use the original) and re-fire the reminder.
                    </p>
                    <Input
                      value={retryEmail}
                      onChange={(e) => setRetryEmail(e.target.value)}
                      placeholder={timelineDrawer.reminder.recipient || 'accountant@example.com'}
                      className="h-8 text-xs"
                      data-testid="timeline-retry-email"
                    />
                    <Button
                      size="sm"
                      className="w-full h-8"
                      disabled={retryLoading || !!retryCooldown}
                      data-testid="timeline-retry-btn"
                      onClick={async () => {
                        setRetryLoading(true);
                        try {
                          const { data: res } = await api.post(
                            `/vat-tracker/reminder-log/${timelineDrawer.reminder.id}/resend`,
                            { override_email: retryEmail || undefined },
                          );
                          toast.success(`Resent to ${res.recipient}`);
                          setRetryEmail('');
                          setRetryCooldown(null);
                          setTimelineDrawer({ open: false, reminder: null });
                          fetchReminderLog();
                        } catch (e) {
                          const det = e?.response?.data?.detail;
                          if (e?.response?.status === 429 && typeof det === 'object' && det.retry_at) {
                            // iter924 — Capture the exact retry_at
                            // timestamp for the ticking countdown.
                            setRetryCooldown({
                              until: det.retry_at,
                              remaining_s: det.retry_after_seconds,
                            });
                            toast.error(det.message || 'Retry limit reached');
                          } else {
                            toast.error(typeof det === 'string' ? det : (det?.message || 'Resend failed'));
                          }
                        } finally {
                          setRetryLoading(false);
                        }
                      }}
                    >
                      {retryLoading ? 'Sending…' :
                        retryCooldown ? `Try again in ${Math.floor((retryCooldown.remaining_s || 0) / 60)}m ${((retryCooldown.remaining_s || 0) % 60).toString().padStart(2, '0')}s` :
                        'Resend now'}
                    </Button>
                    {retryCooldown && (
                      <p className="text-[10px] text-rose-700 dark:text-rose-300 flex items-center gap-1" data-testid="timeline-retry-cooldown">
                        ⏱ Rate limit hit — 3 attempts per hour. Retry unlocks at {new Date(retryCooldown.until).toLocaleTimeString()}.
                      </p>
                    )}
                  </div>
                )}
                {/* Prior retry attempts (if any). */}
                {Array.isArray(timelineDrawer.reminder.resend_attempts) && timelineDrawer.reminder.resend_attempts.length > 0 && (
                  <div className="rounded-lg border p-2 space-y-1" data-testid="timeline-retry-history">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Retry attempts</div>
                    {timelineDrawer.reminder.resend_attempts.map((a, i) => (
                      <div key={i} className="text-[11px] flex items-center justify-between gap-2">
                        <span className="truncate">{a.recipient}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${a.status === 'sent' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>{a.status}</span>
                        <span className="text-muted-foreground shrink-0">{new Date(a.at).toLocaleTimeString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="pt-3 border-t text-[11px] text-muted-foreground grid grid-cols-2 gap-2">
                  <div><span className="opacity-70">Recipient</span><br /><span className="font-medium break-words">{timelineDrawer.reminder.recipient || '—'}</span></div>
                  <div><span className="opacity-70">Email id</span><br /><span className="font-mono text-[10px] break-words">{timelineDrawer.reminder.email_id || '—'}</span></div>
                  <div><span className="opacity-70">Sent at</span><br />{timelineDrawer.reminder.sent_at ? new Date(timelineDrawer.reminder.sent_at).toLocaleString() : '—'}</div>
                  <div><span className="opacity-70">Est. net VAT</span><br />{cur(tenantCurrency)} {(timelineDrawer.reminder.net_vat_estimate || 0).toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
